
import EventBus from "../lib/event-bus";
import { isIconSvg, Notification } from "../lib/types/misc/Notification";
import Vue from "vue";
import Component from "vue-class-component";
import { MixedVue, mixins } from "@/vue";
import { propsInstance } from "@/lib/util";

const Props = propsInstance<{
  duration?: number;
}>({
  duration: {
    type: Number,
    default: 4000,
  },
});

interface InternalNotification extends Notification {
  timerStart: number;
  timerPaused: boolean;
  timerId: number;
  initialY: number;
  ref: HTMLElement;
  threshold: number;
}

@Component
export default class OgorNotifications extends mixins(MixedVue, Props) {
  activeNotifications: Record<number, InternalNotification> = {};
  autoIncrementId: number = 999;

  mounted() {
    EventBus.$on("emitNotification", this.addNotification);
  }

  addNotification(notification: Notification) {
    if (notification.unique) {
      const notificationIds = Object.keys(this.activeNotifications);
      if (notificationIds.find((id) => this.activeNotifications[id].text == notification.text)) {
        return;
      }
    }
    if (notification.duration == null) {
      notification.duration = this.duration;
    }
    let notificationId = this.autoIncrementId;
    this.activeNotifications = {
      ...this.activeNotifications,
      [notificationId]: notification as InternalNotification,
    };
    this.startNotificationTimer(notificationId);
    this.autoIncrementId--;
  }
  removeNotification(notificationId: number) {
    this.$delete(this.activeNotifications, notificationId);
  }
  actionClick(notificationId: number, event: MouseEvent) {
    const notification = this.activeNotifications[notificationId];
    if (!notification) return;
    if (notification.onAction) {
      notification.onAction(event);
    }
    this.removeNotification(notificationId);
  }
  dismissNotification(notificationId: number, event: MouseEvent | TouchEvent) {
    const notification = this.activeNotifications[notificationId];
    if (!notification) return;
    if (notification.onDismiss) {
      notification.onDismiss(event);
    }
    this.removeNotification(notificationId);
  }

  isIconSvg = isIconSvg;

  getMaterialIcon(icon: Notification["icon"]) {
    if (typeof icon == "string") {
      return icon;
    } else if (typeof icon == "object") {
      return ("material" in icon && icon.material) || null;
    }
  }
  startNotificationTimer(notificationId: number) {
    const notification = this.activeNotifications[notificationId];
    if (!notification) return;
    if (notification.duration <= 0) return;
    notification.timerStart = Date.now();
    notification.timerPaused = false;
    notification.timerId = window.setTimeout(() => {
      this.removeNotification(notificationId);
    }, notification.duration);
    this.activeNotifications = {
      ...this.activeNotifications,
      [notificationId]: notification,
    };
  }
  stopNotificationTimer(notificationId: number) {
    const notification = this.activeNotifications[notificationId];
    if (!notification) return;
    if (notification.duration <= 0) return;
    window.clearTimeout(notification.timerId);
    const now = Date.now();
    notification.timerPaused = true;
    const elapsed = now - notification.timerStart;
    notification.duration -= elapsed;
    this.activeNotifications = {
      ...this.activeNotifications,
      [notificationId]: notification,
    };
  }
  touchStart(ev: TouchEvent, id: number) {
    this.stopNotificationTimer(id);
    const notification = this.activeNotifications[id];
    notification.initialY = ev.touches[0].clientY;
    notification.ref = document.getElementById(`notification-${id}`);
    notification.ref.style.transition = "none";
    notification.threshold = -notification.ref.offsetHeight / 2;
  }
  touchMove(ev: TouchEvent, id: number) {
    ev.preventDefault();
    const notification = this.activeNotifications[id];
    const diff = ev.touches[0].clientY - notification.initialY;
    if (diff >= 0) return;
    notification.ref.style.opacity = "" + (1 - diff / notification.threshold);
    notification.ref.style.transform = `translateY(${diff}px)`;
  }
  touchEnd(ev: TouchEvent, id: number) {
    const notification = this.activeNotifications[id];
    const diff = ev.changedTouches[0].clientY - notification.initialY;
    if (diff < notification.threshold) {
      this.dismissNotification(id, ev);
    } else {
      notification.ref.style.transition = null;
      notification.ref.style.opacity = "1";
      notification.ref.style.transform = `translateY(0px)`;
      this.startNotificationTimer(id);
    }
  }
}
